import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { DotLottie } from '@lottiefiles/dotlottie-react';
import { Transaction } from '@mysten/sui/transactions';
import { BullsharkExpression } from '@suigar/suifrens-display-system';
import { useQueryClient } from '@tanstack/react-query';

import { negativeExpressions, positiveExpressions } from '@/components/suifrens/expressions';
import { CASINO_CONFIG } from '@/constants/game-settings';
import { APP_CFG } from '@/configs';
import { mapRawToBetEventDto } from '@/dtos/mapper';
import { useAuth } from '@/hooks';
import { useBoundedNumberStorage } from '@/hooks/useBoundedNumberStorage';
import { useConfetti } from '@/hooks/useConfetti';
import { useCurrency } from './useCurrency';
import { useCustomToast } from './use-suigar-toast';
import { useGameTransactionController } from './useGameTransactionController';
import { useListOfObjects } from './use-list-of-objects';
import { useLoginModalStore } from '@/stores/login-modal-store';
import { usePendingReveal } from './usePendingReveal';
import { useTopGameActivities } from './useTopGameActivities';
import { calculateConfettiBurst } from '@/utils/confetti';
import { createGameSound } from '@/utils/game-sounds';
import { normalizeSuiErrorMessage, isSignatureInvalidError } from '@/utils/errors';
import { filterPendingActivities } from '@/utils/activity';
import { toFloorFixed } from '@/utils';
import { playResultSound } from '@/utils/playResultSound';
import { createSound, sharedLoseSounds, sharedWinSoundsTiered } from '@/utils/sound';
import { winIntensityFromMultiplier } from '@/utils/winIntensity';
import flipSoundSrc from '@/assets/sounds/flip.mp3';
import type { AutoBetExecutorResult } from './useAutoBetRunner';
import { runStandardGameRound } from './runStandardGameRound';

const {
	global: { defaultBetAmount },
} = CASINO_CONFIG;

const {
	bet: { min: MIN_BET, max: MAX_BET },
	sides: { HEADS, TAILS },
} = CASINO_CONFIG.coinflip;

type CoinSide = typeof HEADS | typeof TAILS;

// Define sound effects
const winSounds = sharedWinSoundsTiered;
const loseSounds = sharedLoseSounds;
const flipSound = createGameSound('coinflip', 'flip') ?? createSound(flipSoundSrc, 0.5);

type CoinflipRoundOptions = {
	amount: number;
	side?: CoinSide;
	validators?: Array<() => boolean>;
	onError?: (error: unknown) => void;
	lightning?: boolean;
};

const COINFLIP_STORAGE_KEYS = {
	betAmount: 'suigar.coinflip.bet.amount',
	side: 'suigar.coinflip.side',
} as const;

function getRandomBullsharkExpression(isPositive: boolean): BullsharkExpression {
	return (
		isPositive
			? positiveExpressions[Math.floor(Math.random() * positiveExpressions.length)]
			: negativeExpressions[Math.floor(Math.random() * negativeExpressions.length)]
	) as BullsharkExpression;
}

function getAnimationType({ targetSide, from }: { targetSide: CoinSide; from: CoinSide }) {
	if (targetSide === HEADS) return from === HEADS ? 'headToHead' : 'tailToHead';
	return from === TAILS ? 'tailToTail' : 'headToTail';
}

function useBullsharkIdleAnimation(isAnimating: boolean, setBullsharkExpression: React.Dispatch<React.SetStateAction<BullsharkExpression>>) {
	useEffect(() => {
		const interval = setInterval(() => {
			setBullsharkExpression((prev: BullsharkExpression) =>
				!isAnimating && (prev === 'bigSmile' || prev === 'happy') ? (Math.random() > 0.5 ? 'bigSmile' : 'happy') : prev,
			);
		}, 8000);
		return () => clearInterval(interval);
	}, [isAnimating, setBullsharkExpression]);
}

export const useCoinFlipPage = () => {
	const toast = useCustomToast();
	const { coinFlipActivities, coinFlipActivitiesLoading, setAutoUpdateState } = useListOfObjects();
	const { selectedCurrency, toBaseUnits, fromBaseUnits, formatStandardAmount } = useCurrency();
	const { currentAccount, executeTransaction, displayedBalance, availableBalance, disconnect, balance } = useAuth();
	const { openLoginModal } = useLoginModalStore();
	const queryClient = useQueryClient();
	const [confetti, triggerConfetti] = useConfetti();

	// Game state
	const [betAmount, setBetAmount] = useBoundedNumberStorage({
		storageKey: COINFLIP_STORAGE_KEYS.betAmount,
		defaultValue: defaultBetAmount,
		min: MIN_BET,
		max: MAX_BET,
		round: toFloorFixed,
	});
	const initialSide = useMemo<CoinSide>(() => {
		if (typeof window !== 'undefined') {
			const savedRaw = localStorage.getItem(COINFLIP_STORAGE_KEYS.side);
			if (savedRaw != null) {
				const saved = Number(savedRaw);
				if (Number.isFinite(saved) && (saved === HEADS || saved === TAILS)) {
					return saved as CoinSide;
				}
			}
		}
		return Math.random() > 0.5 ? HEADS : TAILS;
	}, []);
	const initialDisplaySide = useMemo<CoinSide>(() => (Math.random() > 0.5 ? HEADS : TAILS), []);
	const [bullSharkExpression, setBullsharkExpression] = useState<BullsharkExpression>(Math.random() > 0.5 ? 'bigSmile' : 'happy');
	const [from, setFrom] = useState<CoinSide>(initialDisplaySide);
	const [coinSide, setCoinSideState] = useState<CoinSide>(initialSide);
	const [isAnimating, setIsAnimating] = useState<boolean>(false);
	const [currentBalanceBetId, setCurrentBalanceBetId] = useState<string | null>(null);
	const [currentBetEvent, setCurrentBetEvent] = useState<any>(null);
	const [isTransactionPending, setIsTransactionPending] = useState<boolean>(false);
	const animationRef = useRef<DotLottie | null>(null);
	const resultSideRef = useRef<CoinSide>(initialDisplaySide);
	const lightningModeRef = useRef<boolean>(false);
	const animationSpeedRef = useRef<number>(1);
	const animationPlayStartedRef = useRef<boolean>(false);
	const activeAnimationRunIdRef = useRef<number | null>(null);
	const finalizedRunIdRef = useRef<number | null>(null);
	const processedBetIdRef = useRef<string | null>(null);
	const hasPlayedFlipSoundRef = useRef<boolean>(false);

	const setCoinSide = useCallback((value: CoinSide) => {
		setCoinSideState(value);
		if (typeof window !== 'undefined') {
			localStorage.setItem(COINFLIP_STORAGE_KEYS.side, value.toString());
		}
	}, []);
	const { finalize, pendingIdsFor } = usePendingReveal();
	const finalizeResolversRef = useRef<Array<() => void>>([]);
	const { runGamePlay: runCoinflipGame } = useGameTransactionController('coinflip', {
		setTransactionPending: setIsTransactionPending,
		onRequireLogin: openLoginModal,
	});

	// Bullshark idle animation
	useBullsharkIdleAnimation(isAnimating, setBullsharkExpression);

	const resolveFinalizeWaiters = useCallback(() => {
		if (finalizeResolversRef.current.length > 0) {
			finalizeResolversRef.current.forEach((resolver) => resolver());
			finalizeResolversRef.current = [];
		}
	}, []);

	const waitForFinalize = useCallback(
		() =>
			new Promise<void>((resolve) => {
				finalizeResolversRef.current.push(resolve);
			}),
		[],
	);

	const finalizeRound = useCallback(() => {
		const runId = activeAnimationRunIdRef.current;
		if ((runId == null && finalizedRunIdRef.current === -1) || (runId != null && finalizedRunIdRef.current === runId)) {
			return;
		}

		finalizedRunIdRef.current = runId ?? -1;
		if (currentBalanceBetId) {
			finalize({
				balanceBetId: currentBalanceBetId,
				betId: currentBetEvent?.betId || currentBetEvent?.txId,
			});
			setCurrentBalanceBetId(null);
		}
		activeAnimationRunIdRef.current = null;
		setAutoUpdateState(true);
		setIsAnimating(false);
		setFrom(resultSideRef.current);
		const didWin = typeof currentBetEvent?.win === 'boolean' ? (currentBetEvent?.win as boolean) : Number(currentBetEvent?.reward ?? 0) > 0;
		let winIntensity = 0;
		if (didWin) {
			const rewardBaseUnits = Number(currentBetEvent?.reward ?? 0);
			const stakeBaseUnits = Number(currentBetEvent?.amount ?? 0);
			const rewardValue = fromBaseUnits(rewardBaseUnits);
			const stakeValue = fromBaseUnits(stakeBaseUnits);
			const multiplier = stakeValue > 0 ? rewardValue / stakeValue : 0;
			winIntensity = winIntensityFromMultiplier(multiplier, 4);
			const { intensity, duration } = calculateConfettiBurst({ rewardValue, stakeValue, multiplier });
			triggerConfetti({ intensity, duration });
		}
		setBullsharkExpression(getRandomBullsharkExpression(didWin));
		playResultSound({ isWin: didWin, winSounds, loseSounds, options: { context: 'coinflip', winIntensity } });
		animationRef.current?.setSpeed?.(1);
		lightningModeRef.current = false;
		animationSpeedRef.current = 1;
		animationPlayStartedRef.current = false;
		hasPlayedFlipSoundRef.current = false;
		resolveFinalizeWaiters();
	}, [
		currentBalanceBetId,
		currentBetEvent,
		finalize,
		fromBaseUnits,
		resolveFinalizeWaiters,
		setAutoUpdateState,
		setBullsharkExpression,
		setFrom,
		setIsAnimating,
		triggerConfetti,
	]);

	const handleAnimationStart = useCallback(() => {
		if (activeAnimationRunIdRef.current == null || animationPlayStartedRef.current) return;

		animationPlayStartedRef.current = true;
		if (!hasPlayedFlipSoundRef.current) {
			flipSound?.play?.();
			hasPlayedFlipSoundRef.current = true;
		}
	}, []);

	const playSegment = useCallback(({ start, end, direction }: { start: number; end: number; direction?: 1 | -1 }) => {
		const instance = animationRef.current as any;
		if (!instance) return false;

		instance.stop?.();
		instance.setSpeed?.(animationSpeedRef.current);
		instance.setSegment?.(start, end);
		instance.play?.();
		return true;
	}, []);

	const SEGMENTS = useMemo(
		() => ({
			headToHead: { start: 95, end: 190, direction: 1 as const },
			headToTail: { start: 190, end: 285, direction: 1 as const },
			tailToTail: { start: 285, end: 380, direction: 1 as const },
			tailToHead: { start: 0, end: 95, direction: 1 as const },
		}),
		[],
	);

	// Handle transaction success
	useEffect(() => {
		if (!executeTransaction.isSuccess || !currentBetEvent) return;

		const betEventId = currentBetEvent?.betId || currentBetEvent?.txId;
		if (!betEventId || processedBetIdRef.current === betEventId) return;
		processedBetIdRef.current = betEventId;

		const didWin = typeof currentBetEvent.win === 'boolean' ? currentBetEvent.win : Number(currentBetEvent.reward ?? 0) > 0;
		const targetSide = didWin ? coinSide : coinSide === HEADS ? TAILS : HEADS;
		const isLightning = lightningModeRef.current;

		const animationType = getAnimationType({
			targetSide,
			from,
		});

		const segment =
			animationType === 'headToHead'
				? SEGMENTS.headToHead
				: animationType === 'headToTail'
					? SEGMENTS.headToTail
					: animationType === 'tailToTail'
						? SEGMENTS.tailToTail
						: SEGMENTS.tailToHead;

		const runId = Date.now();
		activeAnimationRunIdRef.current = runId;
		finalizedRunIdRef.current = null;
		resultSideRef.current = targetSide;
		hasPlayedFlipSoundRef.current = false;
		animationPlayStartedRef.current = false;
		setIsAnimating(true);
		setBullsharkExpression('shocked');
		animationSpeedRef.current = isLightning ? 2.5 : 1;
		if (!playSegment(segment)) {
			finalizeRound();
			return;
		}
		handleAnimationStart();
	}, [SEGMENTS, coinSide, currentBetEvent, executeTransaction.isSuccess, from, handleAnimationStart, playSegment, finalizeRound]);

	const handleAnimationComplete = useCallback(() => {
		if (activeAnimationRunIdRef.current == null || !animationPlayStartedRef.current) return;

		finalizeRound();
	}, [finalizeRound]);

	const handleAnimationError = useCallback(() => {
		if (activeAnimationRunIdRef.current == null) return;

		finalizeRound();
	}, [finalizeRound]);

	// Change side handler
	const handleChangeSide = (e: React.MouseEvent, value: CoinSide) => {
		if (!isAnimating) {
			setCoinSide(value);
			e.preventDefault();
			e.stopPropagation();
		}
	};

	// Filtered activities
	const shownCoinFlipActivities = useMemo(
		() => filterPendingActivities(coinFlipActivities, pendingIdsFor('coinflip'), { includeTxId: true }),
		[coinFlipActivities, pendingIdsFor],
	);

	// Legacy v1 transaction creation (backwards compatibility)
	const createPlayTransaction = (stake: number) => {
		const txb = new Transaction();

		const coin = txb.splitCoins(txb.gas, [stake]);
		txb.moveCall({
			arguments: [txb.object(APP_CFG.COINFLIP_COINFLIPGAME_SUI_SUI), txb.object(APP_CFG.HOUSE_HOUSE_SUI_SUI), coin, txb.object('0x08')],
			target: `${APP_CFG.PACKAGE_2}::coinflip::place_bet_and_reveal_onchain_randomness`,
			typeArguments: [selectedCurrency.type],
		});

		return txb;
	};

	const parseResponse = (response: any) => {
		const status = (response as any)?.effects?.status?.status;
		if (status === 'failure') {
			const error = normalizeSuiErrorMessage((response as any)?.effects?.status?.error || 'Unknown error', {
				currencySymbol: selectedCurrency.symbol,
			});
			toast({ title: 'Transaction failed', description: error, status: 'error' });
			throw Error(error);
		}

		const events = (response as any)?.events || [];
		const preferred = events.find(
			(e: any) => typeof e?.type === 'string' && e.type.includes('::core::BetResultEvent') && e.type.includes('::coinflip::CoinFlip'),
		);
		const betEvent = preferred || events[events.length - 1];
		if (!betEvent) throw new Error('No event found in response');

		const digest = betEvent?.id?.txDigest || (response as any)?.effects?.transactionDigest;
		const raw = betEvent.parsedJson || {};

		let mapped;
		// Coinflip v2: outcome_amounts + total_stake
		if (Array.isArray(raw.outcome_amounts) || raw.total_stake != null) {
			const outcomeAmounts = Array.isArray(raw.outcome_amounts) ? (raw.outcome_amounts as string[]) : [];
			const totalReward = outcomeAmounts.reduce((acc: bigint, v: string) => acc + BigInt(v || '0'), 0n);
			const rewardNumber = Number(totalReward);
			const stakeNumber = Number(raw.total_stake || 0);
			mapped = {
				betId: digest,
				amount: String(stakeNumber),
				gambler: raw.player,
				reward: rewardNumber,
				win: rewardNumber > 0,
				txId: digest,
			};
		} else {
			mapped = mapRawToBetEventDto(raw, digest);
		}

		if (currentAccount?.address) {
			queryClient.invalidateQueries({ queryKey: ['questStatuses', 'coinflip', currentAccount.address] });
		}

		return mapped;
	};

	const runCoinflipRound = useCallback(
		async ({ amount, side, validators, onError, lightning = false }: CoinflipRoundOptions): Promise<AutoBetExecutorResult> => {
			lightningModeRef.current = lightning;
			animationSpeedRef.current = lightning ? 2.5 : 1;
			if (side != null) {
				setCoinSide(side);
			}

			return runStandardGameRound({
				betAmount: amount,
				runGamePlay: runCoinflipGame,
				toBaseUnits,
				currencyType: selectedCurrency.type,
				validators: validators ?? [() => !isAnimating && !isTransactionPending],
				createTransaction: (stakeBaseUnits) => createPlayTransaction(stakeBaseUnits),
				parseResponse,
				getBetId: (betEvent) => betEvent.betId || betEvent.txId,
				getRewardBaseUnits: (betEvent) => Number(betEvent?.reward ?? 0),
				getDidWin: (betEvent) => Boolean(betEvent?.win),
				onBalanceReserved: ({ balanceBetId }) => {
					setCurrentBalanceBetId(balanceBetId);
				},
				onAfterSuccess: async ({ parsed }) => {
					setCurrentBetEvent(parsed);
					await waitForFinalize();
				},
				onError: (error) => {
					animationPlayStartedRef.current = false;
					animationRef.current = null;
					if (isSignatureInvalidError(error)) {
						disconnect();
						openLoginModal({ sessionExpired: true });
					} else {
						console.log('Error during coinflip play:', error);
						const description = normalizeSuiErrorMessage(error, {
							currencySymbol: selectedCurrency.symbol,
							fallback: 'An unknown error occurred',
						});
						toast({
							title: 'Transaction Failed',
							description,
							status: 'error',
						});
					}

					onError?.(error);
				},
			});
		},
		[
			createPlayTransaction,
			disconnect,
			isAnimating,
			isTransactionPending,
			openLoginModal,
			parseResponse,
			runCoinflipGame,
			selectedCurrency.type,
			selectedCurrency.symbol,
			toBaseUnits,
			toast,
			waitForFinalize,
		],
	);

	const handleFlipClick = async (options?: { lightning?: boolean }) => {
		try {
			await runCoinflipRound({ amount: betAmount, side: coinSide, lightning: options?.lightning });
		} catch {
			// Errors are surfaced via toast within runCoinflipRound.
		}
	};

	const { data: topCoinFlipActivities, isLoading: topCoinFlipActivitiesLoading } = useTopGameActivities('coinflip', { limit: 12 });

	return {
		// Original functions
		coinFlipActivities,
		coinFlipActivitiesLoading,
		setAutoUpdateState,
		createPlayTransaction,
		parseResponse,
		topCoinFlipActivities,
		topCoinFlipActivitiesLoading,
		// Game state and configuration
		HEADS,
		TAILS,
		MAX_BET,
		MIN_BET,
		betAmount,
		setBetAmount,
		initialSide,
		bullSharkExpression,
		setBullsharkExpression,
		from,
		setFrom,
		coinSide,
		setCoinSide,
		isAnimating,
		currentBalanceBetId,
		setCurrentBalanceBetId,
		currentBetEvent,
		setCurrentBetEvent,
		isTransactionPending,
		setIsTransactionPending,
		animationRef,
		// Handlers and effects
		handleAnimationStart,
		handleAnimationComplete,
		handleAnimationError,
		handleChangeSide,
		handleFlipClick,
		runCoinflipRound,
		shownCoinFlipActivities,
		// Additional utils
		confetti,
		balance,
		displayedBalance,
		availableBalance,
		formatStandardAmount,
		toBaseUnits,
		fromBaseUnits,
		selectedCurrency,
		executeTransaction,
		toFloorFixed,
	};
};
